import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MapPin, Truck, Wrench } from "lucide-react";
import { fetchOrder, formatNZD } from "@/lib/api";

export default function Confirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchOrder(orderId).then(setOrder).catch(() => setFailed(true));
  }, [orderId]);

  const auckland = order && order.delivery === "auckland";
  const installed = order && order.delivery === "installation";

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 sm:px-8">
      <div className="py-16 sm:py-20">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <CheckCircle2 className="h-14 w-14 text-volt" strokeWidth={1.8} />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mt-6 font-display text-4xl font-black tracking-tight text-ink sm:text-5xl"
          data-testid="confirmation-heading"
        >
          Order placed.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mt-3 text-base text-zinc-600"
          data-testid="confirmation-subline"
        >
          {failed ? (
            <>We couldn't load this order — but if you reached here from checkout, it's saved.</>
          ) : order ? (
            <>Confirmation emailed to <span className="text-ink">{order.customer.email}</span>. Here's what happens next: we confirm stock with our supplier, then email you a manual invoice. Once paid, {installed ? "drop your bike to us (or we receive your kit) — we build it and you pick up the finished ride" : auckland ? "we'll arrange your Auckland pickup or drop-off" : "we ship it straight to your door"}.</>
          ) : (
            <>Loading your order…</>
          )}
        </motion.p>
      </div>

      {order && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="rounded-2xl border border-black/10 bg-white p-6 sm:p-8"
          data-testid="confirmation-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-2xl font-bold text-volt" data-testid="confirmation-order-id">
              {order.id}
            </p>
            <span className="rounded-full border border-volt/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-volt">
              {order.status === "new" ? "Received" : order.status}
            </span>
          </div>

          <ul className="mt-6 space-y-4" data-testid="confirmation-items">
            {order.items.map((item, i) => (
              <li key={i} className="flex items-center gap-4">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-black/10 bg-surface">
                  <img src={item.image} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-ink">
                    {item.name} <span className="text-zinc-500">× {item.qty}</span>
                  </p>
                  <p className="mt-0.5 truncate text-[11px] text-zinc-500">
                    {item.detail_lines.map((l) => l.name).join(" · ")}
                  </p>
                </div>
                <p className="shrink-0 font-mono text-sm text-zinc-700">{formatNZD(item.line_total)}</p>
              </li>
            ))}
          </ul>

          <div className="mt-6 space-y-2.5 border-t border-black/10 pt-5 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-600">Subtotal</span>
              <span className="font-mono text-ink">{formatNZD(order.subtotal)}</span>
            </div>
            {!installed && (
              <div className="flex justify-between">
                <span className="text-zinc-600 flex items-center gap-2">
                  {auckland ? (
                    <MapPin className="h-4 w-4 text-volt" />
                  ) : (
                    <Truck className="h-4 w-4 text-volt" />
                  )}
                  Delivery ({auckland ? "Auckland pickup/drop-off" : "ship to door"}
                  {order.discount_code ? ` — ${order.discount_code} ${order.discount_pct}% off` : ""})
                </span>
                <span className="font-mono text-ink">{formatNZD(order.shipping)}</span>
              </div>
            )}
            {order.installation_fee > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-600 flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-volt" />
                  Installation (we build it)
                </span>
                <span className="font-mono text-ink" data-testid="confirmation-installation-fee">
                  {formatNZD(order.installation_fee)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-black/10 pt-4">
              <span className="font-display font-bold text-zinc-600">Total</span>
              <span className="font-mono text-xl font-bold text-volt" data-testid="confirmation-total">
                {formatNZD(order.total)} {order.currency}
              </span>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-black/10 bg-surface p-4 text-xs leading-relaxed text-zinc-600">
            {installed ? "Auckland installation — we'll arrange drop-off details with you — " : auckland ? "Pickup/drop-off in Auckland — " : "Shipping to: "}
            {order.shipping_address.street}
            {order.shipping_address.suburb ? `, ${order.shipping_address.suburb}` : ""}
            {", "}
            {order.shipping_address.city}
            {order.shipping_address.postcode ? ` ${order.shipping_address.postcode}` : ""}
          </div>
          {installed && (order.dropoff_date || order.dropoff_time) && (
            <div className="mt-3 rounded-lg border border-volt/30 bg-volt/5 p-4 text-xs leading-relaxed text-zinc-600" data-testid="confirmation-dropoff">
              <span className="font-bold text-ink">Preferred drop-off:</span>{" "}
              {[order.dropoff_date, order.dropoff_time].filter(Boolean).join(" · ")} — we'll confirm the details by email.
            </div>
          )}
        </motion.div>
      )}

      <div className="mt-10 flex flex-wrap gap-4">
        <Link
          to="/shop"
          data-testid="confirmation-continue-shop"
          className="rounded-[3px] bg-volt px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
        >
          Keep Browsing
        </Link>
        <Link
          to="/"
          data-testid="confirmation-home"
          className="rounded-[3px] border border-black/25 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:border-volt hover:text-volt"
        >
          Back Home
        </Link>
      </div>
    </div>
  );
}
